/* -------------------- Focus Trap -------------------- */
export const trapFocus = (element: HTMLElement): (() => void) => {
    const focusableElements = element.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    const firstFocusable = focusableElements[0] as HTMLElement;
    const lastFocusable = focusableElements[
        focusableElements.length - 1
    ] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey && document.activeElement === firstFocusable) {
            lastFocusable.focus();
            e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === lastFocusable) {
            firstFocusable.focus();
            e.preventDefault();
        }
    };

    element.addEventListener('keydown', handleTabKey);
    if (firstFocusable) {
        setTimeout(() => firstFocusable.focus(), 50);
    }

    return () => element.removeEventListener('keydown', handleTabKey);
};

/* -------------------- Dialog Focus Management -------------------- */
export const manageDialogFocus = (
    dialog: HTMLElement,
    isOpen: boolean,
): (() => void) => {
    if (!isOpen) return () => {};

    const previouslyFocused = document.activeElement as HTMLElement;
    const cleanupFocusTrap = trapFocus(dialog);

    return () => {
        cleanupFocusTrap();
        previouslyFocused?.focus();
    };
};

/* -------------------- Unique ID Generator -------------------- */
export const generateId = (prefix: string = 'id'): string => {
    return `${prefix}-${Math.random().toString(36).slice(2, 11)}`;
};

/* -------------------- Reduced Motion Query -------------------- */
export const prefersReducedMotion = (): boolean => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};
